import React, { useState, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import {
  PhotoIcon,
  DocumentIcon,
  LinkIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const PlotAttachments = ({ plotId, attachments = [], onRefresh }) => {
  const { token } = useContext(AppContext);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addType, setAddType] = useState('image');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const fileInputRef = useRef(null);

  // Staged file for upload (not auto-uploaded)
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedFileData, setStagedFileData] = useState(null);

  const images = attachments.filter(a => a.attachmentType === 'image');
  const documents = attachments.filter(a => a.attachmentType === 'document');
  const links = attachments.filter(a => a.attachmentType === 'link');

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Stage file but don't upload yet
    const reader = new FileReader();
    reader.onload = (event) => {
      setStagedFile(file);
      setStagedFileData(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!stagedFile || !stagedFileData) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/plot-attachments/${plotId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attachmentType: addType,
          title: title || stagedFile.name,
          description,
          fileName: stagedFile.name,
          fileType: stagedFile.type,
          fileData: stagedFileData,
          fileSize: stagedFile.size,
          isPublic
        })
      });

      if (!response.ok) throw new Error('Upload failed');
      setShowAddForm(false);
      resetForm();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLink = async () => {
    if (!url) return;
    setIsLoading(true);
    try {
      const response = await fetch(`/api/plot-attachments/${plotId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attachmentType: 'link',
          title: title || url,
          description,
          url,
          isPublic
        })
      });
      if (!response.ok) throw new Error('Failed to add link');
      setShowAddForm(false);
      resetForm();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error adding link:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetch(`/api/plot-attachments/${plotId}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleEditSave = async (attachment) => {
    try {
      const response = await fetch(`/api/plot-attachments/${plotId}/${attachment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          isPublic: attachment.isPublic
        })
      });
      if (!response.ok) throw new Error('Update failed');
      setEditingId(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating attachment:', error);
    }
  };

  const startEditing = (attachment) => {
    setEditingId(attachment.id);
    setEditTitle(attachment.title || '');
    setEditDescription(attachment.description || '');
  };

  const togglePublic = async (attachment) => {
    try {
      await fetch(`/api/plot-attachments/${plotId}/${attachment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          isPublic: !attachment.isPublic
        })
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error toggling public:', error);
    }
  };

  const openImagePreview = async (index) => {
    const img = images[index];
    try {
      const response = await fetch(`/api/plot-attachments/${plotId}/${img.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPreviewImage(data.fileData);
      setPreviewIndex(index);
    } catch (error) {
      console.error('Error loading image:', error);
    }
  };

  const navigatePreview = (direction) => {
    const newIndex = previewIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      openImagePreview(newIndex);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setUrl('');
    setIsPublic(false);
    setAddType('image');
    setStagedFile(null);
    setStagedFileData(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Attachments</h4>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 text-xs text-brand-green-600 hover:text-brand-green-700"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-3 mb-3 border">
          <div className="flex gap-2 mb-3">
            {['image', 'document', 'link'].map(type => (
              <button
                key={type}
                onClick={() => { setAddType(type); setStagedFile(null); setStagedFileData(null); }}
                className={`flex-1 py-1.5 px-2 text-xs rounded-md flex items-center justify-center gap-1 ${
                  addType === type ? 'bg-brand-green-600 text-white' : 'bg-white border text-gray-600'
                }`}
              >
                {type === 'image' && <PhotoIcon className="h-3.5 w-3.5" />}
                {type === 'document' && <DocumentIcon className="h-3.5 w-3.5" />}
                {type === 'link' && <LinkIcon className="h-3.5 w-3.5" />}
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Title (optional)"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full mb-2 px-2 py-1.5 text-sm border rounded-md"
          />

          <input
            type="text"
            placeholder="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full mb-2 px-2 py-1.5 text-sm border rounded-md"
          />

          {addType === 'link' ? (
            <>
              <input
                type="url"
                placeholder="https://..."
                value={url}
                onChange={e => setUrl(e.target.value)}
                className="w-full mb-2 px-2 py-1.5 text-sm border rounded-md"
              />
              <button
                onClick={handleAddLink}
                disabled={!url || isLoading}
                className="w-full py-1.5 text-sm bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:bg-gray-300"
              >
                {isLoading ? 'Adding...' : 'Add Link'}
              </button>
            </>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={addType === 'image' ? 'image/*' : '*'}
                onChange={handleFileSelect}
                className="w-full mb-2 text-sm"
              />
              {/* Staged file preview */}
              {stagedFile && (
                <div className="mb-2">
                  {addType === 'image' && stagedFileData && (
                    <div className="w-20 h-20 rounded overflow-hidden border mb-1">
                      <img src={stagedFileData} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="text-xs text-gray-500">{stagedFile.name} ({(stagedFile.size / 1024).toFixed(1)} KB)</p>
                </div>
              )}
              <button
                onClick={handleUpload}
                disabled={!stagedFile || isLoading}
                className="w-full py-1.5 text-sm bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:bg-gray-300"
              >
                {isLoading ? 'Uploading...' : 'Upload'}
              </button>
            </>
          )}

          <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={e => setIsPublic(e.target.checked)}
              className="h-3.5 w-3.5 text-brand-green-600 rounded"
            />
            Show on public portal
          </label>
        </div>
      )}

      {/* Images - shown as small thumbnails */}
      {images.length > 0 && (
        <div className="mb-3">
          <div className="grid grid-cols-4 gap-1.5">
            {images.map((img, idx) => (
              <div key={img.id} className="relative group">
                {editingId === img.id ? (
                  <div className="col-span-4 bg-gray-50 p-2 rounded border space-y-1">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      placeholder="Title"
                      className="w-full px-2 py-1 text-xs border rounded"
                    />
                    <input
                      type="text"
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      placeholder="Description"
                      className="w-full px-2 py-1 text-xs border rounded"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditSave(img)}
                        className="flex-1 py-1 text-xs bg-brand-green-600 text-white rounded hover:bg-brand-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex-1 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                    <button
                      onClick={() => openImagePreview(idx)}
                      className="w-full h-full"
                    >
                      <img
                        src={`/api/plot-attachments/${plotId}/${img.id}/thumbnail`}
                        alt={img.title || 'Image'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback: load full image if thumbnail not available
                          e.target.onerror = null;
                          e.target.src = '';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                        }}
                      />
                    </button>
                    {/* Action buttons */}
                    <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEditing(img)}
                        className="p-0.5 bg-white/80 text-gray-600 rounded hover:bg-white"
                        title="Edit"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(img.id)}
                        className="p-0.5 bg-white/80 text-red-500 rounded hover:bg-white"
                        title="Delete"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                    {/* Public indicator */}
                    {img.isPublic && (
                      <div className="absolute bottom-0.5 left-0.5 px-1 py-0.5 bg-blue-500/80 text-white text-[8px] rounded">
                        Public
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="mb-3 space-y-1">
          {documents.map(doc => (
            <div key={doc.id} className="group">
              {editingId === doc.id ? (
                <div className="bg-gray-50 p-2 rounded border space-y-1">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    placeholder="Title"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="text"
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    placeholder="Description"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditSave(doc)}
                      className="flex-1 py-1 text-xs bg-brand-green-600 text-white rounded hover:bg-brand-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex-1 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                  <DocumentIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-700 flex-1 truncate">{doc.title}</span>
                  <button
                    onClick={() => togglePublic(doc)}
                    className={`text-[9px] px-1 py-0.5 rounded ${doc.isPublic ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}
                    title={doc.isPublic ? 'Public' : 'Private'}
                  >
                    {doc.isPublic ? 'Public' : 'Private'}
                  </button>
                  <button
                    onClick={() => startEditing(doc)}
                    className="p-0.5 text-gray-400 hover:text-brand-green-600 opacity-0 group-hover:opacity-100"
                    title="Edit"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-0.5 text-red-500 opacity-0 group-hover:opacity-100"
                    title="Delete"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div className="space-y-1">
          {links.map(link => (
            <div key={link.id} className="group">
              {editingId === link.id ? (
                <div className="bg-gray-50 p-2 rounded border space-y-1">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    placeholder="Title"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="text"
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    placeholder="Description"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditSave(link)}
                      className="flex-1 py-1 text-xs bg-brand-green-600 text-white rounded hover:bg-brand-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex-1 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                  <LinkIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex-1 truncate"
                  >
                    {link.title}
                  </a>
                  <button
                    onClick={() => togglePublic(link)}
                    className={`text-[9px] px-1 py-0.5 rounded ${link.isPublic ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}
                    title={link.isPublic ? 'Public' : 'Private'}
                  >
                    {link.isPublic ? 'Public' : 'Private'}
                  </button>
                  <button
                    onClick={() => startEditing(link)}
                    className="p-0.5 text-gray-400 hover:text-brand-green-600 opacity-0 group-hover:opacity-100"
                    title="Edit"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(link.id)}
                    className="p-0.5 text-red-500 opacity-0 group-hover:opacity-100"
                    title="Delete"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && !showAddForm && (
        <p className="text-xs text-gray-400 text-center py-2">No attachments yet</p>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" className="max-w-full max-h-[80vh] rounded-lg" />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            {previewIndex > 0 && (
              <button
                onClick={() => navigatePreview(-1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            {previewIndex < images.length - 1 && (
              <button
                onClick={() => navigatePreview(1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotAttachments;
